<script setup lang="ts">
import type { WeComScheduleBoardResponse } from '@/features/wecomScheduleBoard/api'
import type { ScheduleBoardCardItem } from '@/features/wecomScheduleBoard/boardPresentation'
import { computed, onMounted, ref } from 'vue'
import { loadScheduleBoardFromGroupBoard } from '@/features/wecomScheduleBoard/boardAccess'
import {
  buildTwoWeekCalendar,
  filterRelatedScheduleBoardItems,
  formatMonthDayLabel,
  formatWeekdayLabel,
  getBeijingDate,
  groupItemsByDate,
  scheduleStatusLabels,
} from '@/features/wecomScheduleBoard/boardPresentation'
import { getWeComScheduleBoardErrorMessage } from '@/features/wecomScheduleBoard/wecomContext'

type BoardState = 'checking-entry' | 'checking-identity' | 'wrong-entry' | 'loading' | 'ready' | 'empty' | 'error'

const state = ref<BoardState>('checking-entry')
const errorMessage = ref('')
const board = ref<WeComScheduleBoardResponse | null>(null)
const currentUserId = ref<string | null>(null)
const isRelatedOnly = ref(false)
const todayDate = ref(getBeijingDate())
const selectedDate = ref(todayDate.value)

const activeItems = computed(() => {
  const allItems = board.value?.items ?? []
  if (!isRelatedOnly.value) {
    return allItems
  }
  return currentUserId.value
    ? filterRelatedScheduleBoardItems(allItems, currentUserId.value)
    : []
})
const calendarModel = computed(() => buildTwoWeekCalendar(activeItems.value, todayDate.value))
const itemsByDate = computed(() => groupItemsByDate(activeItems.value))

const selectedDayItems = computed<ScheduleBoardCardItem[]>(() => itemsByDate.value.get(selectedDate.value) ?? [])

const selectedDaySummary = computed(() => {
  const date = selectedDate.value
  const dayItem = calendarModel.value.days.find(d => d.date === date)
  const monthDay = formatMonthDayLabel(date)
  const weekdayText = dayItem ? formatWeekdayLabel(date, dayItem.weekday, todayDate.value) : ''
  const isTodayText = date === todayDate.value ? '（今天）' : ''
  const count = selectedDayItems.value.length
  return {
    countText: count > 0 ? `共 ${count} 场出诊` : '暂无安排',
    title: `${monthDay} ${weekdayText}${isTodayText}`,
  }
})

const isViewingToday = computed(() => selectedDate.value === todayDate.value)

function selectDate(date: string) {
  selectedDate.value = date
}

function toggleRelatedFilter() {
  isRelatedOnly.value = !isRelatedOnly.value
}

function statusLabel(status: ScheduleBoardCardItem['status']) {
  return scheduleStatusLabels[status]
}

function statusClass(status: ScheduleBoardCardItem['status']) {
  return `status-${status.toLowerCase()}`
}

async function initialize() {
  state.value = 'checking-entry'
  errorMessage.value = ''
  todayDate.value = getBeijingDate()
  selectedDate.value = todayDate.value
  isRelatedOnly.value = false

  try {
    const result = await loadScheduleBoardFromGroupBoard(
      () => {
        state.value = 'checking-identity'
      },
      () => {
        state.value = 'loading'
      },
    )
    if (!result) {
      state.value = 'wrong-entry'
      return
    }
    board.value = result.board
    currentUserId.value = result.currentUserId
    state.value = board.value.items.length === 0 ? 'empty' : 'ready'

    // If today has no items but future dates do, we can still default to today
    // so user understands context, but they can easily see dots on other days.
  }
  catch (error) {
    state.value = 'error'
    errorMessage.value = getWeComScheduleBoardErrorMessage(error)
  }
}

onMounted(() => {
  void initialize()
})
</script>

<template>
  <main class="schedule-board" aria-label="出诊看板">
    <!-- 初始加载状态：使用 KLLoading grid 纯动画，搭配企微专属品牌蔚蓝，不带冗余文字 -->
    <KLLoading
      class="schedule-board__loading"
      :model-value="state === 'checking-entry' || state === 'checking-identity' || state === 'loading'"
      type="grid"
      :size="38"
    />

    <section v-if="state === 'wrong-entry'" class="schedule-board__message" aria-live="polite">
      <KLIcon name="i-lucide:messages-square" class="schedule-board__message-icon" />
      <strong>请从企微内部群群看板进入</strong>
      <p>普通网页链接不会展示出诊信息。</p>
    </section>

    <section v-else-if="state === 'error'" class="schedule-board__message schedule-board__message--error" aria-live="assertive">
      <KLIcon name="i-lucide:circle-alert" class="schedule-board__message-icon" />
      <strong>出诊看板暂时不可用</strong>
      <p>{{ errorMessage }}</p>
      <button type="button" class="schedule-board__message-action" @click="initialize">
        重新加载
      </button>
    </section>

    <!-- 正常出诊看板视图：上半部双周日历 + 下半部单日出诊列表 -->
    <div v-else-if="state === 'ready' || state === 'empty'" class="schedule-board__container">
      <!-- 上半部分：双周日历卡片 -->
      <section class="schedule-board__calendar-card" aria-label="双周出诊日历">
        <header class="schedule-board__calendar-header">
          <div class="schedule-board__month-label">
            <KLIcon name="i-lucide:calendar" class="schedule-board__month-icon" />
            <span>{{ calendarModel.monthLabel }}</span>
          </div>
          <button
            type="button"
            class="schedule-board__today-btn"
            :class="{ 'schedule-board__today-btn--active': isRelatedOnly }"
            :aria-pressed="isRelatedOnly"
            @click="toggleRelatedFilter"
          >
            {{ isRelatedOnly ? '查看全部' : '与我相关' }}
          </button>
        </header>

        <!-- 星期表头 -->
        <div class="schedule-board__weekday-row" aria-hidden="true">
          <span class="schedule-board__weekday-cell">一</span>
          <span class="schedule-board__weekday-cell">二</span>
          <span class="schedule-board__weekday-cell">三</span>
          <span class="schedule-board__weekday-cell">四</span>
          <span class="schedule-board__weekday-cell">五</span>
          <span class="schedule-board__weekday-cell schedule-board__weekday-cell--weekend">六</span>
          <span class="schedule-board__weekday-cell schedule-board__weekday-cell--weekend">日</span>
        </div>

        <!-- 14天两行网格 -->
        <div class="schedule-board__days-grid" role="grid" aria-label="未来两周日期选择">
          <button
            v-for="day in calendarModel.days"
            :key="day.date"
            type="button"
            class="schedule-board__day-btn"
            :class="{
              'is-selected': day.date === selectedDate,
              'is-today': day.isToday,
              'is-weekend': day.weekday === 6 || day.weekday === 7,
              'has-schedule': day.status !== 'none',
            }"
            :aria-selected="day.date === selectedDate"
            :aria-label="`${day.month}月${day.dayNumber}日，${day.weekdayName}${day.isToday ? '，今天' : ''}${day.scheduleCount > 0 ? `，${day.scheduleCount}场出诊` : '，无出诊'}`"
            @click="selectDate(day.date)"
          >
            <span class="schedule-board__day-num">{{ day.dayNumber }}</span>
            <span v-if="day.isToday" class="schedule-board__today-badge">今</span>
            <!-- 状态圆点 -->
            <span
              v-if="day.status !== 'none'"
              class="schedule-board__day-dot"
              :class="`schedule-board__day-dot--${day.status}`"
            />
            <span v-else class="schedule-board__day-dot-placeholder" />
          </button>
        </div>
      </section>

      <!-- 下半部分：单日出诊明细列表 -->
      <section class="schedule-board__detail-section" aria-label="选定日期的出诊明细">
        <!-- 摘要头 -->
        <div class="schedule-board__summary-header">
          <h2 class="schedule-board__summary-title">
            {{ selectedDaySummary.title }}
          </h2>
          <span class="schedule-board__summary-count">
            {{ selectedDaySummary.countText }}
          </span>
        </div>

        <!-- 当日有排班卡片列表 -->
        <div v-if="state === 'empty'" class="schedule-board__day-empty schedule-board__day-empty--overall" aria-live="polite">
          <KLIcon name="i-lucide:calendar-x-2" class="schedule-board__day-empty-icon" />
          <p class="schedule-board__day-empty-title">
            未来 14 天暂无出诊安排
          </p>
          <p class="schedule-board__day-empty-desc">
            排班有更新时，看板会自动显示最新出诊信息
          </p>
        </div>
        <div v-else-if="selectedDayItems.length > 0" class="schedule-board__card-list">
          <article
            v-for="card in selectedDayItems"
            :key="`${card.date}-${card.startTime}-${card.doctorName}-${card.institutionName}`"
            class="schedule-board__card"
          >
            <!-- 顶栏：机构名称 + 状态胶囊 -->
            <div class="schedule-board__card-top">
              <div class="schedule-board__inst-group">
                <KLIcon name="i-lucide:hospital" class="schedule-board__inst-icon" />
                <strong class="schedule-board__inst-name">{{ card.institutionName }}</strong>
              </div>
              <span class="schedule-board__status" :class="statusClass(card.status)">
                {{ statusLabel(card.status) }}
              </span>
            </div>

            <!-- 主视觉：医生姓名 + 时段 -->
            <div class="schedule-board__card-main">
              <div class="schedule-board__doctor-info">
                <KLIcon name="i-lucide:stethoscope" class="schedule-board__doctor-icon" />
                <span class="schedule-board__doctor-name">{{ card.doctorName }}</span>
              </div>
              <div v-if="card.timeRangeLabel" class="schedule-board__time-info">
                <KLIcon name="i-lucide:clock-4" class="schedule-board__time-icon" />
                <span>{{ card.timeRangeLabel }}</span>
              </div>
            </div>

            <!-- 底部协同元数据：地区经理与助理 -->
            <footer class="schedule-board__card-meta">
              <div class="schedule-board__meta-item">
                <span class="schedule-board__meta-label">地区经理</span>
                <span class="schedule-board__meta-value">{{ card.regionalManagerLabel }}</span>
              </div>
              <span class="schedule-board__meta-divider">|</span>
              <div class="schedule-board__meta-item">
                <span class="schedule-board__meta-label">助理</span>
                <span class="schedule-board__meta-value">{{ card.assistantLabel }}</span>
              </div>
            </footer>
          </article>
        </div>

        <!-- 当日无排班时的单日空状态 -->
        <div v-else class="schedule-board__day-empty">
          <KLIcon name="i-lucide:calendar-check" class="schedule-board__day-empty-icon" />
          <p class="schedule-board__day-empty-title">
            {{ isViewingToday ? '今日暂无出诊安排' : '该日暂无出诊安排' }}
          </p>
          <p class="schedule-board__day-empty-desc">
            可点击上方日历带有指示点的日期查看其他出诊计划
          </p>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.schedule-board {
  position: relative;
  box-sizing: border-box;
  min-height: 100dvh;
  padding: max(14px, env(safe-area-inset-top)) 12px max(24px, env(safe-area-inset-bottom));
  color: oklch(var(--foreground));
  background: oklch(var(--background));
  isolation: isolate;
}

/* 自定义 KLLoading 颜色为看板专属的专业蔚蓝 (Enterprise WeCom Brand Blue) */
:global(.schedule-board__loading) {
  --primary: 0.5 0.16 230 !important;
}

:global(.schedule-board__loading .sk-grid-cube) {
  background-color: oklch(50% 0.16 230deg) !important;
}

.schedule-board::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: "";
  background: linear-gradient(135deg, oklch(var(--background)) 0%, oklch(97% 0.025 220deg / 72%) 54%, oklch(var(--background)) 100%);
}

.schedule-board__container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

/* 全局加载/错误状态卡片 */
.schedule-board__message {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  min-height: 58vh;
  padding: 28px 20px;
  text-align: center;
  background: oklch(var(--card) / 75%);
  border: 1px solid oklch(var(--border) / 70%);
  border-radius: 20px;
  box-shadow: 0 14px 40px oklch(25% 0.03 250deg / 6%);
  backdrop-filter: blur(14px);
}

.schedule-board__message p {
  max-width: 300px;
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: oklch(var(--muted-foreground));
}

.schedule-board__message-icon {
  width: 32px;
  height: 32px;
  color: oklch(var(--muted-foreground));
}

.schedule-board__message--error .schedule-board__message-icon {
  color: oklch(var(--destructive));
}

.schedule-board__message-action {
  min-height: 36px;
  padding: 0 16px;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 650;
  color: oklch(var(--foreground));
  cursor: pointer;
  background: oklch(var(--card));
  border: 1px solid oklch(var(--border));
  border-radius: 999px;
  transition: background-color 150ms ease;
}

.schedule-board__message-action:active {
  background: oklch(var(--muted));
}

/* 上半部分：日历卡片 */
.schedule-board__calendar-card {
  box-sizing: border-box;
  padding: 14px 12px 12px;
  background: oklch(var(--card) / 94%);
  border: 1px solid oklch(var(--border) / 80%);
  border-radius: 18px;
  box-shadow: 0 8px 24px oklch(25% 0.03 250deg / 7%);
  backdrop-filter: blur(16px);
}

.schedule-board__calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px 10px;
}

.schedule-board__month-label {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 15px;
  font-weight: 750;
  color: oklch(var(--foreground));
}

.schedule-board__month-icon {
  width: 17px;
  height: 17px;
  color: oklch(52% 0.12 220deg);
}

.schedule-board__today-btn {
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  color: oklch(var(--muted-foreground));
  cursor: pointer;
  background: transparent;
  border: 1px solid oklch(var(--border) / 60%);
  border-radius: 999px;
  opacity: 1;
  transition: all 160ms ease;
}

.schedule-board__today-btn--active {
  color: oklch(50% 0.14 220deg);
  cursor: pointer;
  background: oklch(95% 0.03 220deg / 80%);
  border-color: oklch(80% 0.06 220deg);
  opacity: 1;
}

.schedule-board__today-btn--active:active {
  transform: scale(0.96);
}

/* 星期行 */
.schedule-board__weekday-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 4px;
  text-align: center;
}

.schedule-board__weekday-cell {
  padding: 4px 0;
  font-size: 12px;
  font-weight: 600;
  color: oklch(var(--muted-foreground));
}

.schedule-board__weekday-cell--weekend {
  color: oklch(62% 0.08 220deg);
}

/* 日期网格 */
.schedule-board__days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.schedule-board__day-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 4px 0;
  color: oklch(var(--foreground));
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;
}

.schedule-board__day-btn:active {
  transform: scale(0.94);
}

.schedule-board__day-btn.is-weekend {
  color: oklch(48% 0.06 220deg);
}

.schedule-board__day-num {
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

/* 选中高亮状态 */
.schedule-board__day-btn.is-selected {
  color: #fff;
  background: oklch(50% 0.16 230deg);
  border-color: oklch(50% 0.16 230deg);
  box-shadow: 0 4px 12px oklch(50% 0.16 230deg / 35%);
}

.schedule-board__day-btn.is-today:not(.is-selected) {
  border-color: oklch(52% 0.12 220deg / 55%);
}

.schedule-board__day-btn.is-selected.is-today {
  box-shadow: 0 0 0 2px oklch(52% 0.12 220deg / 45%), 0 4px 12px oklch(50% 0.16 230deg / 35%);
}

.schedule-board__day-btn.is-selected .schedule-board__day-num {
  font-weight: 800;
}

/* 今天徽记 */
.schedule-board__today-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: oklch(54% 0.16 225deg);
  border-radius: 999px;
  transform: scale(0.85);
}

.schedule-board__day-btn.is-selected .schedule-board__today-badge {
  color: oklch(50% 0.16 230deg);
  background: #fff;
}

/* 状态指示小圆点 */
.schedule-board__day-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  transition: background-color 150ms ease;
}

.schedule-board__day-dot-placeholder {
  width: 5px;
  height: 5px;
}

.schedule-board__day-dot--scheduled {
  background-color: #10b981; /* 正常出诊：绿色小圆点 */
}

.schedule-board__day-dot--has_leave {
  background-color: #f59e0b; /* 请假/异动：橙色小圆点 */
}

.schedule-board__day-btn.is-selected .schedule-board__day-dot {
  background-color: #fff; /* 选中时变成纯白小圆点 */
}

/* 下半部分：出诊列表 */
.schedule-board__detail-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-board__summary-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0 4px;
}

.schedule-board__summary-title {
  margin: 0;
  font-size: 15px;
  font-weight: 750;
  color: oklch(var(--foreground));
  letter-spacing: -0.01em;
}

.schedule-board__summary-count {
  font-size: 12px;
  font-weight: 600;
  color: oklch(var(--muted-foreground));
}

.schedule-board__card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-board__card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: oklch(var(--card) / 96%);
  border: 1px solid oklch(var(--border) / 75%);
  border-radius: 14px;
  box-shadow: 0 6px 18px oklch(25% 0.03 250deg / 6%), 0 1px 2px oklch(25% 0.03 250deg / 4%);
}

.schedule-board__card-top {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
}

.schedule-board__inst-group {
  display: flex;
  flex: 1 1 auto;
  gap: 6px;
  align-items: center;
  min-width: 0;
}

.schedule-board__inst-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: oklch(52% 0.09 220deg);
}

.schedule-board__inst-name {
  min-width: 0;
  font-size: 15px;
  font-weight: 750;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.schedule-board__status {
  flex: 0 0 auto;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  border: 1px solid transparent;
  border-radius: 999px;
}

.schedule-board__card-main {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
}

.schedule-board__doctor-info {
  display: flex;
  flex: 1 1 180px;
  gap: 6px;
  align-items: center;
  min-width: 0;
}

.schedule-board__doctor-icon {
  width: 18px;
  height: 18px;
  color: oklch(50% 0.16 230deg);
}

.schedule-board__doctor-name {
  min-width: 0;
  font-size: 17px;
  font-weight: 800;
  color: oklch(var(--foreground));
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}

.schedule-board__time-info {
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: oklch(var(--muted-foreground));
}

.schedule-board__time-icon {
  width: 14px;
  height: 14px;
}

.schedule-board__card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding-top: 8px;
  font-size: 12px;
  color: oklch(var(--muted-foreground));
  border-top: 1px solid oklch(var(--border) / 45%);
}

.schedule-board__meta-item {
  display: flex;
  flex: 1 1 auto;
  gap: 5px;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.schedule-board__meta-label {
  color: oklch(var(--muted-foreground) / 80%);
}

.schedule-board__meta-value {
  min-width: 0;
  font-weight: 600;
  color: oklch(var(--foreground) / 90%);
  overflow-wrap: anywhere;
}

.schedule-board__meta-divider {
  color: oklch(var(--border));
  opacity: 0.8;
}

/* 状态色定义 */
.status-scheduled {
  color: rgb(21 104 67);
  background: rgb(220 252 231 / 90%);
  border-color: rgb(134 239 172 / 80%);
}

.status-on_leave {
  color: rgb(146 64 14);
  background: rgb(255 237 213 / 95%);
  border-color: rgb(253 186 116 / 80%);
}

.status-cancelled {
  color: rgb(127 29 29);
  background: rgb(254 226 226 / 95%);
  border-color: rgb(252 165 165 / 80%);
}

/* 单日空状态 */
.schedule-board__day-empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 36px 20px;
  text-align: center;
  background: oklch(var(--card) / 60%);
  border: 1px dashed oklch(var(--border) / 80%);
  border-radius: 16px;
}

.schedule-board__day-empty--overall {
  min-height: 160px;
  padding-block: 28px;
}

.schedule-board__day-empty-icon {
  width: 28px;
  height: 28px;
  color: oklch(var(--muted-foreground) / 70%);
}

.schedule-board__day-empty-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: oklch(var(--foreground));
}

.schedule-board__day-empty-desc {
  max-width: 240px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: oklch(var(--muted-foreground));
}

@media (width >= 760px) {
  .schedule-board {
    padding-top: 24px;
    padding-bottom: 36px;
  }

  .schedule-board__calendar-card {
    padding: 16px 18px 14px;
  }

  .schedule-board__card {
    padding: 16px 20px;
  }
}
</style>
